import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logActivity, formatDateTime } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import type { ContactRequest } from "@/lib/types";
import {
  PageHeader,
  Card,
  Spinner,
  EmptyState,
  Badge,
  ConfirmDialog,
  Button,
  Field,
  Select,
  Modal,
} from "@/pages/admin/components/ui";

const STATUSES = ["nuevo", "contactado", "en_proceso", "completado", "archivado"];

const statusBadge: Record<string, "green" | "primary" | "amber" | "secondary" | "red"> = {
  nuevo: "green",
  contactado: "primary",
  en_proceso: "amber",
  completado: "secondary",
  archivado: "red",
};

const statusLabel: Record<string, string> = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  en_proceso: "En proceso",
  completado: "Completado",
  archivado: "Archivado",
};

export default function Solicitudes() {
  const { success, error: toastError } = useToast();
  const [items, setItems] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<ContactRequest | null>(null);
  const [deleting, setDeleting] = useState<ContactRequest | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from("contact_requests").select("*").order("created_at", { ascending: false });
    setItems((data ?? []) as ContactRequest[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const newCount = items.filter((c) => c.estado === "nuevo").length;

  const filtered = useMemo(() => {
    return items.filter((c) => {
      if (filter !== "todos" && c.estado !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        const haystack = `${c.nombre} ${c.email || ""} ${c.empresa || ""} ${c.tipo_proyecto || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter, search]);

  async function changeStatus(c: ContactRequest, estado: string) {
    const { error } = await supabase.from("contact_requests").update({ estado }).eq("id", c.id);
    if (error) {
      toastError("No se pudo actualizar el estado.");
      return;
    }
    await logActivity("cambió el estado de una solicitud", c.nombre);
    success("Estado actualizado.");
    setDetail({ ...c, estado });
    load();
  }

  async function confirmDelete() {
    if (!deleting) return;
    const { error } = await supabase.from("contact_requests").delete().eq("id", deleting.id);
    if (error) {
      toastError("No se pudo eliminar la solicitud.");
      return;
    }
    await logActivity("eliminó una solicitud", deleting.nombre);
    success("Solicitud eliminada.");
    setDeleting(null);
    load();
  }

  return (
    <div>
      <PageHeader
        title="Solicitudes"
        subtitle={`${newCount} solicitud${newCount === 1 ? "" : "es"} nueva${newCount === 1 ? "" : "s"}`}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, email, empresa…"
          className="w-full sm:max-w-xs px-3.5 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full sm:w-auto">
          <option value="todos">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{statusLabel[s]}</option>
          ))}
        </Select>
      </div>

      {loading ? (
        <Spinner label="Cargando solicitudes…" />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon="ri-mail-line"
            title="No hay solicitudes"
            description="Cuando alguien complete el formulario de contacto aparecerá aquí."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <Card key={c.id} className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 font-semibold text-sm">
                {c.nombre.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-foreground-900">{c.nombre}</p>
                  <Badge tone={statusBadge[c.estado] || "secondary"}>{statusLabel[c.estado] || c.estado}</Badge>
                </div>
                <p className="text-xs text-foreground-500 truncate">
                  {[c.tipo_proyecto, c.email, c.empresa].filter(Boolean).join(" · ") || "Sin detalles"}
                </p>
              </div>
              <span className="text-xs text-foreground-400 whitespace-nowrap shrink-0">{formatDateTime(c.created_at)}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setDetail(c)} className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-50"><i className="ri-eye-line" /></button>
                <button onClick={() => setDeleting(c)} className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title="Detalle de solicitud"
        maxWidth="max-w-lg"
        footer={
          detail && (
            <>
              <Button variant="danger" onClick={() => { setDeleting(detail); setDetail(null); }}>Eliminar</Button>
              <Button onClick={() => setDetail(null)}>Cerrar</Button>
            </>
          )
        }
      >
        {detail && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Field label="Estado">
                <Select value={detail.estado} onChange={(e) => changeStatus(detail, e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{statusLabel[s]}</option>)}
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs text-foreground-400">Nombre</p><p className="text-sm text-foreground-900">{detail.nombre}</p></div>
              <div><p className="text-xs text-foreground-400">Empresa</p><p className="text-sm text-foreground-900">{detail.empresa || "—"}</p></div>
              <div><p className="text-xs text-foreground-400">Email</p><p className="text-sm text-foreground-900">{detail.email || "—"}</p></div>
              <div><p className="text-xs text-foreground-400">WhatsApp</p><p className="text-sm text-foreground-900">{detail.whatsapp || "—"}</p></div>
              <div><p className="text-xs text-foreground-400">Tipo de proyecto</p><p className="text-sm text-foreground-900">{detail.tipo_proyecto || "—"}</p></div>
              <div><p className="text-xs text-foreground-400">Presupuesto</p><p className="text-sm text-foreground-900">{detail.presupuesto || "—"}</p></div>
              <div className="col-span-2"><p className="text-xs text-foreground-400">Fecha</p><p className="text-sm text-foreground-900">{formatDateTime(detail.created_at)}</p></div>
              <div className="col-span-2"><p className="text-xs text-foreground-400">Mensaje</p><p className="text-sm text-foreground-900 whitespace-pre-wrap">{detail.mensaje || "—"}</p></div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={Boolean(deleting)} title="Eliminar solicitud" message={`¿Seguro que quieres eliminar la solicitud de "${deleting?.nombre}"?`} onConfirm={confirmDelete} onCancel={() => setDeleting(null)} />
    </div>
  );
}